import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  Button,
  Select,
  FormControl,
  FormLabel,
  TextareaAutosize,
  Divider,
  //CircularProgress,
} from '@mui/material';
//import CancelIcon from '@mui/icons-material/Cancel';
import { useDispatch, useSelector } from "react-redux";
import { haversineDistance } from '../../utils/utils';
import { useForm, Controller } from 'react-hook-form';
import { updateTaskAsync, updateTaskStatusAsync } from '../../redux/slices/tasksSlice';
import { setNotification } from '../../redux/slices/notificationSlice';
import { formatDate } from '../../utils/utils';
import MapAppChooser from './PatrolUI/MapAppChooser';
import RecoveryMap from '../map/RecoveryMap';
import TaskProperty from './PatrolUI/TaskProperty';
import TaskStatusSelector from './TaskStatusSelector';

const TaskCard = ({ task, patrol, getCurrentTask }) => {
  const dispatch = useDispatch();
  const [showMapChooser, setShowMapChooser] = useState(false);
  const [showMapChooserRec, setShowMapChooserRec] = useState(false);
  //const [appleMapsLink, setAppleMapsLink] = useState('');
  //const [wazeLink, setWazeLink] = useState('');
  const [googleMapsLink, setGoogleMapsLink] = useState('');
  const [googleMapsDeepLink, setGoogleMapsDeepLink] = useState('');
  //const [appleMapsLinkRec, setAppleMapsLinkRec] = useState('');
  //const [wazeLinkRec, setWazeLinkRec] = useState('');
  const [copilotLink, setCopilotLink] = useState('');
  const [copilotRecLink, setCopilotRecLink] = useState('');
  const [googleMapsLinkRec, setGoogleMapsLinkRec] = useState('');
  const [showCompletionOptions, setShowCompletionOptions] = useState(false);
  const [showRecoveryOptions, setShowRecoveryOptions] = useState(false);
  const [showRecoveryMap, setShowRecoveryMap] = useState(false);
  const [distance, setDistance] = useState('0');
  const [taskStatuses, setTaskStatuses] = useState([]);
  const { taskStatuses: tstatuses, resourceStatuses: pstatuses, completionCodes, vehicleMakes, vehicleModels, vehicleColours, vehicleFaults, taskTypes } = useSelector((state) => state.constants?.data);
  //const taskLoading = useSelector(state => state.tasks.loading);
  const loading = useSelector(state => state.tasks.loading);
  const defaultStatusFormValues = useMemo(() => ({
    statusId: '',
    completionCode: '',
    completionPartsSummary: '',
    completionNotes: '',
    recoveryDestination: {},
    recoveryDestinationString: '',
    recoveryDestinationText: '',
  }), []);
  const [comments, setComments] = useState(task?.comments);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(task.recoveryDestination);

  const { control, handleSubmit, reset, setValue, formState, watch } = useForm({ defaultValues: defaultStatusFormValues });
  const { isDirty } = formState;
  const recoveryDestinationText = watch('recoveryDestinationText');
  useEffect(() => {
    if (task && !isDirty) {
      reset(task, {keepDirty: true, keepDirtyValues: true}); 
    }
  }, [task, reset, isDirty]);

  useEffect(() => {
    setTaskStatuses([...tstatuses].filter(t => !['GDET', 'GARR', 'GHED', 'GRCY', 'UNAC', 'CLSD'].includes(t.code)).sort((a, b) => a.code.toLowerCase().localeCompare(b.code.toLowerCase())));
  }, []);

  useEffect(() => {
    let location;
    if (patrol.location && patrol.location?.lat) {
      location = patrol.location;
    } else {
      if (patrol.baseLocation && patrol.baseLocation?.lat) {
        location = {
          lat: patrol.baseLocation.lat,
          lng: patrol.baseLocation.lng
        }
      }
    }
    setDistance(Math.round(haversineDistance(location.lat, location.lng, task.location.lat, task.location.lng) * 0.6213));
    // if (task && task.recoveryDestination && task.recoveryDestination?.lat != null) {

    //   setRecoveryDistance(Math.round(haversineDistance(location.lat, location.lng, task.recoveryDestination.lat, task.recoveryDestination.lng) * 0.6213));
    // }
    setComments(task.comments);

    location = null;

    setGoogleMapsLink(`https://maps.google.com?q=${task?.location?.lat},${task?.location?.lng}`);
    setGoogleMapsDeepLink(`comgooglemaps://?q=${task?.location?.lat},${task?.location?.lng}`);
    setCopilotLink(`copilot://mydestination?type=LOCATION&action=VIEW&lat=${task?.location?.lat}&long=${task?.location?.lng}`);

    setGoogleMapsLinkRec(`https://maps.google.com?q=${task.recoveryDestination?.lat},${task.recoveryDestination?.lng}`);
    setCopilotRecLink(`copilot://mydestination?type=LOCATION&action=VIEW&lat=${task?.recoveryDestination?.lat}&long=${task?.recoveryDestination?.lng}`);

    setValue('recoveryDestinationString', `${task.recoveryDestination.lat}, ${task.recoveryDestination.lng}`);

    setSelectedAddress(task.recoveryDestinationText);
  }, [patrol, task]);

  const handleComments = () => {
    dispatch(updateTaskAsync({ ...task, comments }));
  }

  const updateRecoveryLocation = async (data) => {
    const result = await dispatch(updateTaskAsync({
      ...task,
      typeId: 2,
      recoveryDestination: data.recoveryDestination,
      recoveryDestinationText: data.recoveryDestinationText

    })).unwrap();
    if (result.status == 200 || result.status == 204) {      
      dispatch({
        type: 'tasks/updateTask',
        payload: { ...data, typeId: 2 },
      });
      dispatch(
        setNotification({ severity: 'success', message: 'Task recovery location updated.' })
      );
    }

  }

  const updateTaskStatus = async (data) => {
    const newStatus = taskStatuses.find(s => s.id == data.statusId).code;
    const result = await dispatch(updateTaskStatusAsync({
      id: data.id,
      statusCode: newStatus,
      completionCode: data.completionCode,
      completionPartsSummary: data.completionPartsSummary,
      completionNotes: data.completionNotes,
      comments: data.comments
    }));
    if (!result.error && !result.error?.message) {
      dispatch(
        setNotification({ severity: 'success', message: 'Task status updated.' })
      );
    } else {
      setValue('statusId', task?.statusId, {shouldDirty: true, shouldValidate: true});
      dispatch(
        setNotification({ severity: 'error', message: result.payload.message })
      );
    }

    return result;
  }

  const onSubmit = async (data) => {
    try {
      if (data.statusId == 26) {
        if (!data.recoveryDestinationText || !data.recoveryDestination) {
          dispatch(
            setNotification({ severity: 'error', message: 'Recovery destination cannot be empty when updating to RECY.' })
          );
          return;
        }
        await updateRecoveryLocation(data);
        if (task.statusId != data.statusId) {
          await updateTaskStatus(data);
        }
      } else {
        const result = await updateTaskStatus(data);
        if (!result.error && !result.error?.message) {

          dispatch({
            type: 'tasks/updateTask',
            payload: data,
          });
          const tstatus = tstatuses.find(s => s.id == data.statusId);
          if (tstatus) {
            const pstatus = pstatuses.find(p => p.code == tstatus.code);
            if (pstatus) {
              const updatedPatrol = { ...patrol, status: pstatus }
              dispatch({
                type: 'patrols/updatePatrol',
                payload: updatedPatrol,
              });
            }
          }
        }
      }
      getCurrentTask();

    } catch (error) {
      dispatch(
        setNotification({ severity: 'error', message: error.message })
      );
    }
  }

  const onError = (errors) => {
    console.log('ERRORS', errors);
  }

  const handleCloseMapChooser = () => { }

  const handleSelectAddress = (address) => {
    setSelectedAddress(address);
    setValue('recoveryDestinationText', address);
  }

  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
    setValue('recoveryDestination', {
      lat: location.lat,
      lng: location.lng
    });
  }

  const setNextStatus = (newStatus) => {
    setValue('statusId', newStatus, {shouldDirty: true, shouldValidate: true});
    if (newStatus == 1) {
      setShowCompletionOptions(true)
    } else {
      setShowCompletionOptions(false)
    }
    if (newStatus == 26) {
      setShowRecoveryOptions(true)
    } else {
      setShowRecoveryOptions(false)
    }
  }

  const commentsRef = useRef(null);

  if (loading) return <>Loading assigned task data...</>
  return (
    <>
      <MapAppChooser
        //appleMapsLink={appleMapsLink}
        googleMapsLink={googleMapsLink}
        googleMapsDeepLink={googleMapsDeepLink}
        //wazeLink={wazeLink}
        copilotLink={copilotLink}
        setShowMapChooser={setShowMapChooser}
        showMapChooser={showMapChooser}
        handleCloseMapChooser={handleCloseMapChooser}
      />
      <MapAppChooser
        //appleMapsLink={appleMapsLinkRec}
        googleMapsLink={googleMapsLinkRec}
        //wazeLink={wazeLinkRec}
        copilotLink={copilotRecLink}
        setShowMapChooser={setShowMapChooserRec}
        showMapChooser={showMapChooserRec}
        handleCloseMapChooser={handleCloseMapChooser}
      />

      <RecoveryMap
        task={task}
        showRecoveryMap={showRecoveryMap}
        setShowRecoveryMap={setShowRecoveryMap}
        selectedAddress={selectedAddress}
        setSelectedAddress={handleSelectAddress}
        selectedLocation={selectedLocation}
        setSelectedLocation={handleSelectLocation}
      />

      {/* Task basic info && Task Status */}
      <Box sx={{
        borderRadius: '16px',
        bgcolor: 'background.white',
        width: '92%',
        mx: 'auto',
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Box sx={{
          width: '100%',
          mt: '20px',
          backgroundImage: "url('data:image/svg+xml;base64,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')",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '4% 50%',
          paddingLeft: '60px',
        }}>
          <Typography sx={{ fontSize: '24px', color: 'background.black', fontWeight: 700 }}>Task: {task.id} ({taskTypes.find(t => t.id == task?.typeId)?.name})</Typography>
        </Box>

        <TaskProperty
          property='Created on'
          value={formatDate(task.createdOn, true)}
          sx={{
            marginTop: '20px',
            pb: '15px',
          }}
        />

        <TaskProperty
          property='Customer name'
          value={task?.customerName}
          sx={{
            marginTop: '5px',
            pb: '15px',
          }}
        />

        <Box sx={{
          width: '100%',
          marginTop: '0px',
          pb: '16px',
          display: 'flex',
          alignContent: 'center',
          flexDirection: 'column',
          paddingLeft: '20px',
          paddingRight: '20px',
          mx: 'auto'
        }}>
          <Typography sx={{ fontSize: '20px', color: 'background.black', fontWeight: 700, marginBottom: '10px' }}>
            Next task status
          </Typography>

          {/* Status form */}
          <Box>

            <form style={{ width: '100%', maxWidth: '330px' }} onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 2 }}>
                <Box sx={{ width: '100%' }}>

                  <TaskStatusSelector taskStatuses={tstatuses} currentStatus={task?.statusId} setNextStatus={setNextStatus} />
                  <Controller
                    name="statusId"
                    control={control}
                    render={() => null}
                  />
                </Box>
              </Box>

              { showCompletionOptions && (
                <Box
                  sx={{
                    mt: 2,
                  }}
                >
                  <Controller
                    name="completionCode"
                    control={control}
                    rules={{
                      required: "Enter completion reason."
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <FormLabel sx={{ mb: '-8px' }}>Completion Reason</FormLabel>
                        <Select
                          required
                          className='comp-select'
                          label="Completion Reason"
                          sx={{
                            pt: 1,
                            mb: 1,
                            '& ul': {
                              overflow: 'auto'
                            },
                            '& .MuiInputLabel-asterisk': { color: '#DA0076' }
                          }}
                          fullWidth
                          {...field}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        >
                          {completionCodes.map((reason) => (
                            <MenuItem key={reason.code} value={reason.code}>
                              {reason.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography className="comp-errors">{fieldState.error?.message}</Typography>
                      </>
                    )}
                  />

                  <Controller
                    name="completionPartsSummary"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth sx={{ mb: 2 }} >
                        <FormLabel>Completed with parts</FormLabel>
                        <TextareaAutosize
                          minRows={3}
                          placeholder="Add part info here"
                          style={{
                            width: '100%',
                            padding: 8,
                            fontSize: 16,
                            borderRadius: 4
                          }}
                          {...field}
                        />
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="completionNotes"
                    control={control}
                    rules={{
                      required: "Enter completion notes."
                    }}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth>
                        <FormLabel>Completion Notes</FormLabel>
                        <TextareaAutosize
                          required
                          minRows={3}
                          placeholder="Completion Notes"
                          style={{
                            width: '100%',
                            padding: '8px',
                            fontSize: '16px',
                            borderRadius: '4px',
                            marginBottom: '10px',
                            borderColor: fieldState.error ? '#DA0076' : '#b5b5b5'
                          }}
                          {...field}
                        />
                        <Typography className="comp-errors">{fieldState.error?.message}</Typography>
                      </FormControl>
                    )}
                  />
                </Box>
              )}

              {(showRecoveryOptions || task?.typeId == 2) && (
                <>
                  <Divider sx={{ mt: '20px', width: '100%' }} />
                  <Box sx={{
                    width: '100%',
                    marginTop: '0px',
                    pb: '16px',
                    display: 'flex',
                    alignContent: 'center',
                    flexDirection: 'column',
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    mx: 'auto'
                  }}>

                    <Typography sx={{ mt: '5px', fontSize: '20px', color: 'background.black', fontWeight: 700, marginBottom: '10px' }}>
                      Recovery location
                    </Typography>

                    {recoveryDestinationText &&
                      <Typography sx={{ mt: '5px', fontSize: '16px', color: '#2B2929', fontWeight: 400, marginBottom: '10px', fontFamily: 'New Transport' }}>
                        {recoveryDestinationText}
                      </Typography>
                    }

                    <Controller
                      name="recoveryDestinationText"
                      control={control}
                      render={() => null}
                    />

                    <Button
                      variant='outlined'
                      sx={{
                        width: '100%',
                        color: "background.black",
                        mx: 'auto'
                      }}
                      onClick={(e) => {
                        e.target.blur();
                        setShowRecoveryMap(true);
                      }}
                    >
                      {recoveryDestinationText ? 'Edit recovery location' : 'Search for recovery location'}
                    </Button>

                    {recoveryDestinationText &&
                      <Box sx={{
                        width: '100%',
                        marginTop: '0px',
                        pb: '25px',
                        display: 'flex',
                        alignContent: 'center'
                      }}>
                        <Button
                          onClick={() => {
                            setShowMapChooserRec(true);
                          }}
                          variant="outlined"
                          sx={{
                            width: '100%',
                            color: "background.black",
                            mx: 'auto',
                            marginTop: '15px'
                          }}>
                          Directions to recovery location
                        </Button>
                      </Box>
                    }

                  </Box>
                  <Divider sx={{ width: '100%' }} />
                </>
              )}
              <Box sx={{ width: '100%' }}>
                <Button
                  loading={loading}
                  sx={{
                    width: '100%',
                    mt: 2,
                    height: '48px',
                    fontSize: '20px'
                  }}
                  onClick={handleSubmit(onSubmit, onError)}
                  type="submit"
                  variant="yellow"
                  color="primary">
                  Update task
                </Button>
              </Box>

            </form>

          </Box>

        </Box>
      </Box>

      {/* Task details */}
      <Box sx={{
        borderRadius: '16px',
        bgcolor: 'background.white',
        width: '92%',
        mx: 'auto',
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        marginTop: '20px'
      }}>
        <Box sx={{
          width: '100%',
          mt: '20px',
          paddingLeft: '20px',
        }}>
          <Typography sx={{ fontSize: '24px', color: 'background.black', fontWeight: 700 }}>Details</Typography>
        </Box>

        {task?.typeId == 2 &&
          <TaskProperty
            property='Recovery location'
            value={task?.recoveryDestinationText}
            firstBoxMargin='0px'
            sx={{
              flexDirection: 'column',
              paddingLeft: '20px',
              marginTop: '17px'
            }}
          />
        }

        <Box sx={{
          width: '100%',
          marginTop: '10px',
          pb: '15px',
          display: 'flex',
          alignContent: 'center',
          flexDirection: 'row'
        }}>
          <Typography sx={{ fontSize: '20px', color: 'background.black', fontWeight: 700, marginLeft: '20px', marginRight: '10px' }}>
            Distance to location:
          </Typography>
          <Typography sx={{ fontSize: '16px', color: 'background.grey', fontWeight: 400, fontFamily: 'New Transport', marginTop: '2px' }}>
            {distance} mi
          </Typography>
        </Box>

        <Box sx={{
          width: '100%',
          marginTop: '0px',
          pb: '25px',
          display: 'flex',
          alignContent: 'center'
        }}>
          <Button
            onClick={() => {
              setShowMapChooser(true);
            }}
            variant="outlined"
            sx={{
              width: '92%',
              color: "background.black",
              mx: 'auto'
            }}>
            Directions to location
          </Button>
        </Box>

        <TaskProperty
          property='Task location'
          value={task?.locationDescription}
          firstBoxMargin='0px'
          sx={{
            flexDirection: 'column',
            paddingLeft: '20px'
          }}
        />

        <Divider />

        <TaskProperty
          property='Fault'
          value={vehicleFaults.find(f => f.id == task?.faultId).name}
        />

        <TaskProperty
          property='Vehicle info'
          value={`${vehicleMakes.find(m => m.id == task?.vehicleMakeId)?.name} - ${vehicleModels.find(m => m.id == task?.vehicleModelId)?.name}`}
        />

        <TaskProperty
          property='Vehicle color'
          value={vehicleColours.find(c => c.name.toLowerCase() == task?.vehicleColour.toLowerCase())?.name}
        />

        <TaskProperty
          property='Vehicle registration no'
          value={task?.vehicleRegistrationNumber}
        />

        <TaskProperty
          property='Customer phone no.'
          value={<a href={`tel:${task?.contactPhoneNumber}`}>{task?.contactPhoneNumber}</a>}
        />

        <Divider />

        <TaskProperty
          property='Priority'
          value={` ${task.isPriority == true ? 'Yes' : 'No'}`}
        />

        <TaskProperty
          property='Home start'
          value={` ${task.isHomestart == true ? 'Yes' : 'No'}`}
        />

        <Divider />

        <Box sx={{
          width: '92%',
          marginTop: '0px',
          pb: '25px',
          display: 'flex',
          alignContent: 'center'
        }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <FormLabel
              sx={{ fontSize: '20px', color: 'background.black', fontWeight: 700, marginRight: '10px' }}
            >
              Comments
            </FormLabel>
            <TextareaAutosize
              minRows={3}
              placeholder="Comments"
              style={{
                width: '100%',
                padding: 8,
                fontSize: 16,
                borderRadius: 4
              }}

              value={comments ?? ''}
              onChange={(e) => {
                setComments(e.target.value);
              }}
            />
          </FormControl>
        </Box>

        <Box sx={{
          width: '92%',
          marginTop: '0px',
          pb: '25px',
          display: 'flex',
          alignContent: 'center'
        }}>
          <Button ref={commentsRef} onClick={handleComments} variant="yellow" sx={{ mt: 0 }}>Add Comments</Button>
        </Box>
      </Box>
    </>
  )
};

export default TaskCard;
